import { omit, debounce } from 'lodash';
import queryString from 'query-string';

import SimilarListing, { ResponseRecord } from 'types/Apis/SearchResponse';
import VehicleWithExperience from 'types/Apis/VehicleWithExperience';
import VehicleApiResponse, { VehicleInfo } from 'types/Apis/VehicleApiResponse';
import { ModelReviewsInterface, SearchURL, DealerReviewsInterface } from 'types/Apis/SearchURL';
import Comparison from 'types/Apis/Comparison';
import { AutolistUser } from 'types/User';
import UserApi from 'types/Apis/UserApi';
import config from 'lib/config';
import {
  SearchParamsV2,
  SearchResultsV2ResponseData,
  VehicleWithExperienceApiResponse,
  SearchV2Result,
  VehicleOverviewsInterface,
  ArticlePreview,
  DrivingExperiences,
  TopDealers,
} from 'lib/AutolistAPI/types';

import { MakeModel } from 'types/srpFilters';
import * as Sentry from '@sentry/nextjs';
import { transformVehicleApiResponse } from 'components/Srp/utils';
import ContentLocation from '../ContentLocation';
import getAuthToken from '../getAuthToken';
import { AdInfo } from '../CarGurusAds';
import {
  transformSrpVehicle,
  transformMakesAndModels,
  transformTextSnippetsApiResponse,
  transformModelReviewsApiResponse,
  transformDealerReviewsApiResponse,
  transformArticlesApiResponse,
  transformDrivingExperiencesApiResponse,
  transformAdInfo,
} from './transformers';

// legacy types

// new types

export * from './types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JSON = Record<string, any>;

function errorWhenStatusCodeNot(response: Response, ...statusCodes: number[]): Response {
  if (!statusCodes.includes(response.status)) {
    throw new Error(`${response.status} fetching ${response.url}`);
  }

  return response;
}

function withAssertSuccess(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await baseFetch(input, init);
    if (!response.ok) {
      const errorMessage = await response.text();
      if (!errorMessage.includes('VehicleNotFound')) {
        Sentry.captureException(new Error(`API error: ${errorMessage}`), {
          tags: {
            serverside: typeof window === 'undefined',
            'http.manually_set_status_code': response.status,
          },
        });
      }
      throw new Error(
        `Got status ${response.status} while fetching ${response.url}: ${errorMessage}`
      );
    }
    return response;
  };
}

// url params is a function so that we can call it at runtime, because things like remote config
// need to be initialized first, which may not have happened when this file is imported,
// and seems to be out of our control
function withBaseUrl(url: () => string, baseFetch: typeof fetch = fetch): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit): ReturnType<typeof fetch> => {
    let newInput: RequestInfo;
    if (typeof input === 'string') {
      newInput = `${url()}${input}`;
    } else {
      const nonStringInput = input as Request;
      newInput = Object.assign({}, nonStringInput, { url: `${url()}${nonStringInput.url}` });
    }
    return baseFetch(newInput, init);
  };
}

export function withAuth(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    const headers = { ...(init.headers || {}), 'x-campaigns': await getAuthToken() };

    return baseFetch(input, { ...init, headers });
  };
}

export function withOptionalAuth(baseFetch: typeof fetch = fetch): typeof fetch {
  return async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    let headers = init.headers || {};

    try {
      const authToken = await getAuthToken();
      headers = { ...headers, 'x-campaigns': authToken };
    } catch (e) {
      console.warn(`unable to get auth token when fetching ${JSON.stringify(input)}: ${e}`);
    }

    return baseFetch(input, { ...init, headers });
  };
}

function withCache(cache: boolean) {
  return function withCacheFetch(baseFetch: typeof fetch = fetch): typeof fetch {
    return async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
      let headers;

      if (cache === false) {
        headers = { ...(init.headers || {}), 'cache-control': 'no-cache ' };
      } else {
        ({ headers } = init);
      }

      return baseFetch(input, { ...init, headers });
    };
  };
}

type JSONFetchFn = (...params: Parameters<typeof fetch>) => Promise<any>;
function withJsonParsing(baseFetch: typeof fetch): JSONFetchFn {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<any> => {
    const r = await baseFetch(input, init);
    return r.json();
  };
}

export const autolistFetch = withBaseUrl(() => config.coreApiUrl, fetch);
export const debouncedAutolistFetch = debounce(
  withJsonParsing(withAssertSuccess(autolistFetch)),
  500
) as JSONFetchFn;
export const defaultAutolistFetch = withJsonParsing(withAssertSuccess(autolistFetch));

const lookupFetch = withBaseUrl(() => config.lookupApiUrl, fetch);
const defaultLookupFetch = withJsonParsing(withAssertSuccess(lookupFetch));

function logStamp(requestId?: string): string {
  return `[${new Date().toISOString()}] ${requestId}`;
}

type TimedFunction = (...args: any[]) => Promise<any>;

export function withTiming(
  apiFn: TimedFunction,
  requestId?: string
): (...args: Parameters<typeof apiFn>) => ReturnType<typeof apiFn> {
  return async (...args: Parameters<typeof apiFn>) => {
    const start = Date.now();
    try {
      const response = await apiFn(...args);
      const duration = Date.now() - start;
      console.log(`${logStamp(requestId)} - ${apiFn.name} took ${duration} milliseconds.`);
      return [response, duration];
    } catch (error) {
      console.log(`${logStamp(requestId)}  - ${apiFn.name} request failed: ${error}`);
      throw error;
    }
  };
}

export interface FetchVehicleWithExperienceParams {
  vin: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  locale: string;
}

export async function fetchVehicleWithExperience(
  params: FetchVehicleWithExperienceParams
): Promise<VehicleWithExperience> {
  const urlSearchParams = queryString.stringify({
    exp_lat: params.latitude,
    exp_lon: params.longitude,
    exp_radius: params.radius,
    exp_locale: params.locale,
    vin: params.vin,
  });
  return (await defaultAutolistFetch(
    `/api/vehicle_with_experience?${urlSearchParams}`
  )) as VehicleWithExperience;
}

export function textSnippetUrl(slug: string): URL {
  const query = new URLSearchParams({ parent_page: slug }).toString();
  const url = new URL(`${config.contentApiUrl}/text_snippets?${query}`);
  return url;
}

export async function fetchVehicleOverview(
  slug: string,
  options = {
    bypassCache: false,
  }
): Promise<VehicleOverviewsInterface | undefined> {
  const url = textSnippetUrl(slug);

  const fetcher = withCache(!options.bypassCache)(fetch);

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(
      `Text Snippets URL API error: ${response.status} ${response.headers} ${response.url}`
    );
  }

  const data = await response.json();
  return transformTextSnippetsApiResponse(data);
}

export async function fetchDealerReviews(
  slug: string
): Promise<DealerReviewsInterface | undefined> {
  const response = await autolistFetch(
    `/api/dealer_reviews?search_url_slug=${encodeURIComponent(slug)}`
  );

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(
      `Dealer Reviews API error: ${response.status} ${response.headers} ${response.url}`
    );
  }

  const data = await response.json();
  return transformDealerReviewsApiResponse(data);
}

export function modelReviewUrl(slug: string, page?: string): URL {
  const query = new URLSearchParams({ search_url_slug: slug, ...(page && { page }) }).toString();
  const url = new URL(`${config.coreApiUrl}/api/model_reviews?${query}`);
  return url;
}

export async function fetchModelReviews(
  slug: string,
  type: string,
  page?: string,
  options = {
    bypassCache: false,
  }
): Promise<ModelReviewsInterface | undefined> {
  // Only these pages should render the OwnerReviews component
  // Any response other than undefined will cause the component to render
  const supportedPages = [
    'MakeModelYearSearchUrl',
    'MakeModelLocationSearchUrl',
    'MakeModelSearchUrl',
    'BodyStyleSearchUrl',
    'MakeSearchUrl',
  ];
  if (!supportedPages.includes(type)) {
    return undefined;
  }
  const url = modelReviewUrl(slug, page);

  const fetcher = withCache(!options.bypassCache)(fetch);

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(
      `Model Reviews URL API error: ${response.status} ${response.headers} ${response.url}`
    );
  }

  const data = await response.json();
  return transformModelReviewsApiResponse(data);
}

export async function fetchArticlePreviewsOfParentPage(
  parentPageSlug: string
): Promise<ArticlePreview[] | undefined> {
  const url = new URL(
    `${config.contentApiUrl}/articles?parent_page=${encodeURIComponent(parentPageSlug)}`
  );

  const response = await fetch(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw new Error(`Vehicle Articles API error: ${await response.text()}`);
  }

  const data = await response.json();
  return transformArticlesApiResponse(data);
}

export async function fetchDrivingExperiencesOfCity(
  cityId: number,
  page?: number
): Promise<DrivingExperiences | undefined> {
  // eslint-disable-next-line no-restricted-globals
  const pageParam = !isNaN(page as number) ? `&page=${page}` : '';
  const response = await autolistFetch(
    `/api/driving_experiences?city_id=${cityId}${pageParam}&per_page=2`
  );

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw new Error(`Driving Experiences API error: ${await response.text()}`);
  }

  const data = await response.json();
  return transformDrivingExperiencesApiResponse(data, cityId);
}

export async function fetchSearchUrl(slug: string, params = {}): Promise<SearchURL | undefined> {
  const query = new URLSearchParams(params).toString();
  const response = await autolistFetch(`/api/search_urls/${slug}?${query}`);

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Search URL API error: ${response.status} ${response.headers} ${response.url}`);
  }

  return response.json();
}

export type ListingsResponse = JSON;

export async function fetchListings(params: JSON): Promise<JSON> {
  const query = queryString.stringify(params);
  return defaultAutolistFetch(`/search?${query}`);
}

export async function fetchMakesAndModels(): Promise<JSON> {
  const headers = { 'Accept-Encoding': 'gzip, deflate' };
  const result = await defaultLookupFetch('/makes', { headers });
  return transformMakesAndModels(result);
}

export async function postLeadFeedback(leadId: number, params: JSON): Promise<JSON> {
  const query = new URLSearchParams(params);
  return defaultAutolistFetch(`/api/leads/${leadId}/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: query.toString(),
  });
}

type AutocompReq = {
  query: string;
  limit: number;
};

export async function locationAutocomplete({
  query,
  limit = 5,
}: AutocompReq): Promise<ContentLocation[]> {
  const urlQuery = new URLSearchParams({
    q: query,
    limit: String(limit),
  }).toString();
  const results = await defaultLookupFetch(`/locations/content-autocomplete?${urlQuery}`);

  return results.map((result: JSON) => {
    return new ContentLocation(result as any);
  });
}

export async function fetchInfographics(): Promise<JSON> {
  return defaultLookupFetch('/infographics');
}

export async function fetchQuickPicks(vin: string): Promise<JSON> {
  return defaultAutolistFetch(`/api/vehicles/${vin}/quick_picks`);
}

export async function fetchArticles(
  params: {
    locale: string;
    parentCategory: string;
    abridged?: boolean;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON> {
  const url = new URL(`${config.contentApiUrl}/articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(params as JSON).toString();

  const response = errorWhenStatusCodeNot(await fetcher(url.toString()), 200);

  return response.json();
}

export async function fetchGuidesFeaturedArticles(
  params: {
    locale: string;
    abridged?: boolean;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON> {
  const url = new URL(`${config.contentApiUrl}/content-landing-pages/guides/featured-articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(params as JSON).toString();

  const response = errorWhenStatusCodeNot(await fetcher(url.toString()), 200);

  return response.json();
}

export async function fetchArticle(
  slug: string,
  params: {
    // eslint-disable-next-line camelcase
    parent_page?: string;
    related?: boolean;
    locale: string;
  },
  options = {
    bypassCache: false,
  }
): Promise<JSON | undefined> {
  const url = new URL(`${config.contentApiUrl}/articles`);

  const fetcher = withCache(!options.bypassCache)(fetch);

  url.search = new URLSearchParams(omit(params) as JSON).toString();
  url.pathname += `/${slug}`;

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Articles API error fetching ${url}`);
  }

  return response.json();
}

export function comparisonUrl(slug: string, params: { locale: string }): URL {
  const url = new URL(`${config.comparisonsApiUrl}/${slug}`);

  url.search = new URLSearchParams(params as JSON).toString();

  return url;
}

export async function fetchComparison(
  slug: string,
  params: {
    locale: string;
  },
  options = {
    bypassCache: false,
  }
): Promise<
  | {
      comparisons: Comparison[];
      comparison: Comparison;
    }
  | undefined
> {
  const url = comparisonUrl(slug, params);

  const fetcher = withCache(!options.bypassCache)(fetch);

  const response = await fetcher(url.toString());

  if (response.status === 404) {
    return undefined;
  }

  if (response.status !== 200) {
    throw new Error(`Comparisons API error fetching ${url}`);
  }

  return response.json();
}

export async function fetchTopDealers(
  latitude: number,
  longitude: number
): Promise<TopDealers | undefined> {
  const response = await autolistFetch(
    `/api/dealer_pages/links?coordinates=${latitude},${longitude}&radius=30&limit=30`
  );

  if (response.status === 404) {
    return undefined;
  }

  if (!response.ok) {
    throw new Error(`Top Dealers API error: ${await response.text()}`);
  }

  const data = await response.json();
  return data.dealer_page_links;
}

export async function fetchAllComparisons(
  params: {
    locale: string;
    abridged?: boolean;
  },
  bypassCache: boolean
): Promise<{ comparisons: Comparison[] }> {
  const url = new URL(`${config.comparisonsApiUrl}`);

  url.search = new URLSearchParams(params as JSON).toString();

  const fetcher = withJsonParsing(withAssertSuccess(withCache(!bypassCache)(fetch)));

  const result = await fetcher(url.toString());

  return result || { comparisons: [] };
}

export async function fetchUser(): Promise<AutolistUser> {
  const url = new URL('/api/current_user', config.coreApiUrl);

  const response = await withAuth(fetch)(url.toString(), {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  const payload = (await response.json()) as UserApi;

  return {
    id: payload.id,
    firstName: payload.first_name || undefined,
    lastName: payload.last_name || undefined,
    email: payload.email,
    phone: payload.phone || undefined,
    zip: payload.zip || undefined,
    loanDetails: {
      interestRate: payload.interest_rate ?? undefined,
      loanDuration: payload.loan_duration || undefined,
      downPayment: payload.down_payment ?? undefined,
      downPaymentPercentage: payload.down_payment_percentage ?? undefined,
      shouldUseDownPaymentPercentage: payload.use_down_payment_percentage ?? undefined,
    },
  };
}

export async function updateUser(params: Partial<UserApi>): Promise<void> {
  const url = new URL('/api/current_user', config.coreApiUrl);

  await withAuth(fetch)(url.toString(), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });
}

export async function fetchSimilarListings(vin: string): Promise<ResponseRecord[]> {
  const response = (await defaultAutolistFetch(`/api/vehicles/${vin}/similar`)) as SimilarListing;
  return response.records;
}

export async function fetchVehicle(vin: string): Promise<VehicleInfo> {
  return transformVehicleApiResponse(await defaultAutolistFetch(`/api/vehicles/${vin}`));
}

export async function searchVehicles(params: SearchParamsV2): Promise<SearchV2Result> {
  const result: SearchResultsV2ResponseData = await defaultAutolistFetch(
    `/api/v2/search?ads=true&include_total_price_change=true&include_time_on_market=true&include_relative_price_difference=true&${queryString.stringify(
      params || {},
      { arrayFormat: 'bracket' }
    )}`
  );

  return {
    vehicles: result.records.map((vehicle) => transformSrpVehicle(vehicle)),
    totalCount: result.total_count,
    adInfo: transformAdInfo(result.ad_info),
  };
}

export interface FetchVehiclesWithExperienceParams {
  vins: string[];
  latitude?: number;
  longitude?: number;
  radius?: number;
  locale: string;
}

// bulk retrieval endpoint: https://www.pivotaltracker.com/n/projects/2406515/stories/180755662
export async function fetchVehiclesWithExperience(
  params: FetchVehiclesWithExperienceParams
): Promise<VehicleWithExperienceApiResponse[]> {
  const promises = params.vins.map((vin) => fetchVehicleWithExperience({ vin, ...params }));

  const vehiclesWithExperience: VehicleWithExperienceApiResponse[] = [];
  const results = await Promise.allSettled<VehicleWithExperienceApiResponse>(promises);
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      vehiclesWithExperience.push(result.value);
    }
  });

  return vehiclesWithExperience;
}

export async function fetchAdInfo(vin: string): Promise<AdInfo | undefined> {
  const response = (await defaultAutolistFetch(
    `/api/vehicles/${vin}?ads=web`
  )) as VehicleApiResponse;
  return response.ad_info;
}

export async function fetchVehicleTrims(makeModelInfo: MakeModel): Promise<string[]> {
  const response = (await defaultAutolistFetch(
    `/trims?make=${makeModelInfo.make}&model=${makeModelInfo.model}`
  )) as string[];
  return response;
}
